/**
 * FeedbackModal — buzón de sugerencias in-app.
 *
 * Feature aislada (diseño aprobado 2026-09-15): captura categoría + mensaje
 * libre y los manda a api/send-feedback.ts, que inserta en Supabase y
 * notifica a delia@tryquipus.com vía Resend. No toca journal_entries,
 * validateEntry(), Triple Write ni AccountingEngine.ts.
 */

import { useState } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { colors } from '../utils/theme';
import { useLang } from '../context/LangContext';
import { t } from '../i18n/dictionary';
import { supabase } from '../services/supabaseClient';
import { showToast } from './ui/Toast';

type Categoria = 'bug' | 'idea' | 'otro';

const CATEGORIAS: Categoria[] = ['bug', 'idea', 'otro'];

const CATEGORIA_KEY: Record<Categoria, string> = {
  bug: 'feedback.categoriaBug',
  idea: 'feedback.categoriaIdea',
  otro: 'feedback.categoriaOtro',
};

interface Props {
  onClose: () => void;
}

export default function FeedbackModal({ onClose }: Props) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const c = colors(isDark);
  const { lang } = useLang();

  const [categoria, setCategoria] = useState<Categoria>('idea');
  const [mensaje, setMensaje] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!mensaje.trim()) {
      setError(t('feedback.mensajeRequired', lang));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error(t('feedback.errorGeneric', lang));
      }
      const res = await fetch('/api/send-feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          categoria,
          mensaje: mensaje.trim(),
          pagina: window.location.pathname,
        }),
      });
      if (!res.ok) {
        throw new Error(t('feedback.errorGeneric', lang));
      }
      showToast(t('feedback.successToast', lang), 'success');
      onClose();
    } catch (err) {
      // Error visible en el modal — nunca cierra en silencio (mismo criterio
      // que TransactionDrawer: un fallo se ve, no se traga).
      setError(err instanceof Error ? err.message : t('feedback.errorGeneric', lang));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div
        className="w-full max-w-[440px] rounded-2xl overflow-hidden shadow-[0_24px_60px_color-mix(in srgb, var(--canvas) 60%, transparent),0_0_0_1px_var(--brand-soft)]"
        style={{ background: c.modal, border: `1px solid ${c.border}` }}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: c.borderSoft }}>
          <div className="w-10 h-10 rounded-xl bg-[var(--brand-soft)] border border-[var(--brand-bd)] flex items-center justify-center flex-shrink-0">
            <MessageSquare size={18} className="text-[var(--link)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="t-h3 font-bold" style={{ color: c.textPrimary }}>{t('feedback.modalTitle', lang)}</div>
            <div className="t-2xs leading-relaxed mt-0.5" style={{ color: c.textSub }}>{t('feedback.modalSubtitle', lang)}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('feedback.close', lang)}
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
            style={{ color: c.textSub }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 flex flex-col gap-4">
          {/* Categoría */}
          <div>
            <label className="t-xs font-bold uppercase tracking-wider block mb-2" style={{ color: c.textSub }}>
              {t('feedback.categoriaLabel', lang)}
            </label>
            <div className="flex gap-2">
              {CATEGORIAS.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategoria(cat)}
                  className="flex-1 px-3 py-2 rounded-lg t-sm font-semibold border transition-colors"
                  style={
                    categoria === cat
                      ? { borderColor: 'var(--brand)', background: 'var(--brand-soft)', color: c.textPrimary }
                      : { borderColor: c.borderInput, background: c.inputBg, color: c.textNormal }
                  }
                >
                  {t(CATEGORIA_KEY[cat], lang)}
                </button>
              ))}
            </div>
          </div>

          {/* Mensaje */}
          <div>
            <label className="t-xs font-bold uppercase tracking-wider block mb-2" style={{ color: c.textSub }}>
              {t('feedback.mensajeLabel', lang)}
            </label>
            <textarea
              value={mensaje}
              onChange={(e) => { setMensaje(e.target.value); if (error) setError(null); }}
              placeholder={t('feedback.mensajePlaceholder', lang)}
              rows={5}
              className="w-full rounded-lg px-3 py-2 t-sm outline-none resize-none"
              style={{ background: c.inputBg, border: `1px solid ${c.borderInput}`, color: c.textPrimary }}
            />
          </div>

          {/* Error visible — nunca se cierra el modal en silencio */}
          {error && (
            <div className="t-xs rounded-lg px-3 py-2" style={{ background: 'var(--surface-raised)', color: c.negative, border: '1px solid var(--surface-raised)' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-2 border-t flex items-center justify-end gap-2" style={{ borderColor: c.borderSoft }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg t-sm font-semibold transition-colors disabled:opacity-50"
            style={{ color: c.textNormal, background: c.subtleBg, border: `1px solid ${c.border}` }}
          >
            {t('feedback.cancel', lang)}
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting}
            className="px-4 py-2 rounded-lg t-sm font-semibold text-white transition-colors disabled:opacity-50"
            style={{ background: 'var(--brand)', color: 'var(--on-brand)' }}
          >
            {submitting ? t('feedback.submitting', lang) : t('feedback.submit', lang)}
          </button>
        </div>
      </div>
    </div>
  );
}
